// @charitystack/org-engagement-contracts — paragon module, SQS effect messages.
//
// The repo's SQS seam contracts, transcribed bug-for-bug from the executable
// legacy contract (`contracts-legacy/paragon-backend/schemas.ts`, pinned
// `origin/dev` = `7cd48d532bf395c1313aa1267b113907ac8e0d57`). Every schema is
// `.strict()`. Queue topology facts (visibility, maxReceiveCount, DLQ names)
// are infra, not schemas — they live in the legacy contract's comments, not here.

import { z } from 'zod';

// ---------------------------------------------------------------------------
// INBOUND to `functions/subscription_trigger/trigger.py:handler`, from
// `{stage}-subscriptions-backend-subscriptionUpdateQueue` (owned by
// subscription-backend, bound BY ARN, batchSize 1). The handler indexes
// `body['merchantName']` and `body['id']` WITHOUT `.get()`, so either key missing
// is a KeyError → the whole batch fails → SQS redelivers. `updated_fields` is an
// open set on the producer side, so unknown keys are possible — but this schema is
// `.strict()` anyway, per the legacy method: an unexpected key is a FINDING to
// investigate against the producer, never a reason to widen.
// ---------------------------------------------------------------------------

export const subscriptionUpdateQueueMessageSchema = z
  .object({
    merchantName: z.string(),
    id: z.string(),
    fundIsUpdated: z.boolean().optional(),
    contactId: z.union([z.string(), z.number(), z.null()]).optional(),
    segmentId: z.union([z.string(), z.number(), z.null()]).optional(),
    newSubscriptionStatus: z.string().optional(),
    newCoverFees: z.union([z.boolean(), z.null()]).optional(),
    newFees: z.union([z.number(), z.string(), z.null()]).optional(),
  })
  .strict();
export type SubscriptionUpdateQueueMessage = z.infer<typeof subscriptionUpdateQueueMessageSchema>;

// ---------------------------------------------------------------------------
// OUTBOUND from `subscription_trigger/trigger.py:send_to_paragon` to
// `POST api.useparagon.com/projects/{PARAGON_PROJECT_ID}/sdk/events/trigger`.
// The `data` dict literal assigns exactly two keys, `name` and `payload`, and the
// payload literal assigns exactly eleven — six read off the subscription row,
// five off the queue message. Serialized with `cls=DecimalEncoder`, so DynamoDB
// numbers arrive as JSON NUMBERS here. Every value is `.get()` with no default,
// so any of them can be `null` when the row or message lacks the key.
// ---------------------------------------------------------------------------

export const paragonSubscriptionUpdateEventSchema = z
  .object({
    name: z.literal('Update subscription'),
    payload: z
      .object({
        dateEndCharge: z.union([z.string(), z.null()]),
        dateNextCharge: z.union([z.string(), z.null()]),
        donationFrequency: z.union([z.string(), z.null()]),
        funds: z.union([z.array(z.unknown()), z.null()]),
        paymentMethod: z.union([z.string(), z.record(z.unknown()), z.null()]),
        amount: z.union([z.number(), z.string(), z.null()]),
        fundIsUpdated: z.union([z.boolean(), z.null()]),
        contactId: z.union([z.string(), z.number(), z.null()]),
        segmentId: z.union([z.string(), z.number(), z.null()]),
        newSubscriptionStatus: z.union([z.string(), z.null()]),
        newCoverFees: z.union([z.boolean(), z.null()]),
        newFees: z.union([z.number(), z.string(), z.null()]),
      })
      .strict(),
  })
  .strict();
export type ParagonSubscriptionUpdateEvent = z.infer<typeof paragonSubscriptionUpdateEventSchema>;

// ---------------------------------------------------------------------------
// INBOUND to `functions/lambda_trigger/trigger.py:handler` via
// `{stage}-paragon-backend-genericParagonQueue` ← SNS `ParagonSNSTopic` ←
// contacts-db-backend `updater.py`. Legacy live queue: `maxReceiveCount` 1 (a
// SINGLE failure DLQs the message — no retry), batchSize 1. DRIFT, recorded not
// fixed: prod `VisibilityTimeout` is 600 s; the legacy stack declares 270 s.
// ---------------------------------------------------------------------------

export const genericParagonQueueMessageSchema = z
  .object({
    baseMerchant: z.string(),
    contactID: z.string(),
    transactionID: z.string(),
    processor: z.string(),
  })
  .strict();
export type GenericParagonQueueMessage = z.infer<typeof genericParagonQueueMessageSchema>;

// ---------------------------------------------------------------------------
// INBOUND to `functions/payouts_trigger/payouts_trigger.py:handler` via
// `{stage}-paragon-backend-payoutsTriggerQueue` ← payouts-backend's Finix/Stripe
// webhooks (targeted by QUEUE NAME, not ARN), plus the handler's own self-requeue.
// Legacy live queue: `maxReceiveCount` 3, visibility 900 s, batchSize 1; the DLQ
// held 1 message at the August capture (legacy finding F7).
//
// The seven optional batch/retry keys exist on the identical current `origin/main`
// and `origin/dev` trees. The dated August production capture found 0 batch-page
// marker lines, but source equality does not establish current deployed behavior.
// They are `.optional()` because the pinned consumer accepts messages both with
// and without them. The five source-consumed integer self-requeue counters
// (`btFetchRetryCount`, `balanceTransactionReadinessRetryCount`,
// `balanceTransactionSnapshotRetryCount`, `netSuiteBatchWaitRetryCount`,
// `netSuiteBatchVerifyRetryCount`) are part of the accepted set as of the
// 2026-09-11 contract refresh.
// ---------------------------------------------------------------------------

export const payoutsTriggerQueueMessageSchema = z
  .object({
    merchant_name: z.string(),
    charity_stack_id: z.string(),
    payout_id: z.string().optional(),
    batch: z.boolean().optional(),
    last_evaluated_key: z.record(z.unknown()).optional(),
    batch_page: z.number().int().optional(),
    verify_netsuite_batch: z.boolean().optional(),
    btFetchRetryCount: z.number().int().optional(),
    balanceTransactionReadinessRetryCount: z.number().int().optional(),
    balanceTransactionSnapshotRetryCount: z.number().int().optional(),
    netSuiteBatchWaitRetryCount: z.number().int().optional(),
    netSuiteBatchVerifyRetryCount: z.number().int().optional(),
  })
  .strict();
export type PayoutsTriggerQueueMessage = z.infer<typeof payoutsTriggerQueueMessageSchema>;
